import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import MetricsContext from '../../context/MetricsContext';
import Emergency from '../../components/Emergency/Emergency';
import PageNames from '../../utils/PageNames';
import PageTypes from '../../utils/PageTypes';
import ButtonNames from '../../utils/ButtonNames';
import { getGeolocationPermissionState, persistUserLocation, requestCurrentLocation } from '../../utils';
import { ensureNotificationSubscription } from '../../utils/notifications';
import styles from './OnboardingSetup.module.css';
import englishFlag from '../../assets/designSystem/icons/United Kingdom.svg';
import swahiliFlag from '../../assets/designSystem/icons/Kenya.svg';
import checkedIcon from '../../assets/designSystem/icons/Checkbox.svg';

const LANGUAGE_OPTIONS = [
  {
    label: 'English',
    value: 'English',
    image: englishFlag,
  },
  {
    label: 'Swahili',
    value: 'Swahili',
    image: swahiliFlag,
  },
];

const LANGUAGE_VALUES = LANGUAGE_OPTIONS.map((option) => option.value);

const getStoredLanguage = () => {
  const stored = localStorage.getItem('i18nextLng');
  return LANGUAGE_VALUES.includes(stored) ? stored : '';
};

function LanguageIcon() {
  return (
    <svg className={styles.sectionIcon} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.6 2.4 4 5.7 4 9s-1.4 6.6-4 9c-2.6-2.4-4-5.7-4-9s1.4-6.6 4-9Z" />
    </svg>
  );
}

function TermsIcon() {
  return (
    <svg className={styles.sectionIcon} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l7 3v5c0 4.8-3 9.2-7 10-4-0.8-7-5.2-7-10V6l7-3Z" />
      <path d="M8.5 12.1l2.2 2.2 4.8-4.8" />
    </svg>
  );
}

const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }

  if (window.Notification.permission === 'granted') {
    try {
      await ensureNotificationSubscription();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error ensuring notification subscription:', error);
    }
    return 'granted';
  }

  if (window.Notification.permission !== 'default') {
    return window.Notification.permission;
  }

  try {
    const permission = await window.Notification.requestPermission();

    if (permission === 'granted') {
      try {
        await ensureNotificationSubscription();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error ensuring notification subscription:', error);
      }
    }

    return permission;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

const requestLocationPermission = async () => {
  const permissionState = await getGeolocationPermissionState();

  if (permissionState === 'unsupported') {
    return 'unsupported';
  }

  if (permissionState === 'denied') {
    return 'denied';
  }

  try {
    const position = await requestCurrentLocation();
    await persistUserLocation(position);
    return 'granted';
  } catch {
    return 'denied';
  }
};

export default function OnboardingSetup() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { initMetrics, updateUserMetric, storeLocalMetrics } = useContext(MetricsContext);
  const startDate = useRef(new Date());
  const initialSessionId = localStorage.getItem('sessionId');
  const sessionId =
    initialSessionId && initialSessionId !== 'undefined' && initialSessionId !== 'null' ? initialSessionId : null;
  const [selectedLanguage, setSelectedLanguage] = useState(getStoredLanguage);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedDataProcessing, setAcceptedDataProcessing] = useState(false);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const metricPayload = {
      name: PageNames.ONBOARDING_SETUP_PAGE,
      pageType: PageTypes.DEFAULT,
    };

    if (sessionId) {
      updateUserMetric(metricPayload);
    } else {
      storeLocalMetrics(metricPayload);
    }
  }, [sessionId]);

  useEffect(() => {
    const requestSetupPermissions = async () => {
      await requestNotificationPermission();
      await requestLocationPermission();
    };

    requestSetupPermissions().catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error requesting setup permissions:', error);
    });
  }, []);

  const canContinue = useMemo(
    () => Boolean(selectedLanguage) && acceptedTerms && acceptedDataProcessing && !isSubmitting,
    [acceptedDataProcessing, acceptedTerms, isSubmitting, selectedLanguage]
  );

  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language);
    i18n.changeLanguage(language);
  };

  const stopCheckboxLinkToggle = (event) => {
    event.stopPropagation();
  };

  const updateMetrics = useCallback(() => {
    const completedAt = new Date();
    const metricPayload = {
      name: PageNames.ONBOARDING_SETUP_PAGE,
      pageType: PageTypes.DEFAULT,
      duration: Math.abs(completedAt - startDate.current),
      interactions: [
        {
          name: `language:${selectedLanguage || 'none'}`,
          time: completedAt,
        },
        {
          name: `terms:${acceptedTerms}`,
          time: completedAt,
        },
        {
          name: `data-processing:${acceptedDataProcessing}`,
          time: completedAt,
        },
        {
          name: ButtonNames.CONTINUE,
          time: completedAt,
        },
      ],
    };

    if (sessionId) {
      updateUserMetric(metricPayload);
    } else {
      storeLocalMetrics(metricPayload);
    }
  }, [acceptedDataProcessing, acceptedTerms, selectedLanguage, sessionId, storeLocalMetrics, updateUserMetric]);

  const handleContinue = async () => {
    if (!canContinue) {
      return;
    }

    setIsSubmitting(true);
    updateMetrics();

    try {
      await initMetrics();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error initializing metrics:', error);
    } finally {
      navigate('/homepage');
    }
  };

  return (
    <div className={`container ${styles.page}`}>
      <main className={`containerContent ${styles.content}`}>
        <header className={styles.header}>
          <h1 className={styles.title}>{t('languagepage.start')}</h1>
          <p className={styles.subtitle}>{t('onboardingSetupPage.intro')}</p>
        </header>

        <section className={styles.section}>
          <div>
            <div className={styles.sectionHeading}>
              <LanguageIcon />
              <h2 className={styles.sectionTitle}>{t('onboardingSetupPage.languageTitle')}</h2>
            </div>
            <p className={styles.sectionDescription}>{t('languagepage.selectlang')}</p>
          </div>

          <div className={styles.languageGrid}>
            {LANGUAGE_OPTIONS.map((option) => {
              const isSelected = selectedLanguage === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.languageCard} ${isSelected ? styles.languageCardSelected : ''}`}
                  onClick={() => handleLanguageSelect(option.value)}
                  aria-pressed={isSelected}
                >
                  <img src={option.image} alt="" className={styles.flagImage} />
                  <span className={styles.languageLabel}>{option.label}</span>
                  <span
                    className={`${styles.languageIndicator} ${isSelected ? styles.languageIndicatorSelected : ''}`}
                    aria-hidden="true"
                  >
                    {isSelected && <img src={checkedIcon} alt="" className={styles.languageIndicatorIcon} />}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeading}>
            <TermsIcon />
            <div>
              <h2 className={styles.sectionTitle}>{t('onboardingSetupPage.termsTitle')}</h2>
            </div>
          </div>

          <div className={styles.checkboxList}>
            <div className={styles.checkboxRow}>
              <input
                id="terms-consent"
                type="checkbox"
                className={styles.checkbox}
                checked={acceptedTerms}
                onChange={() => setAcceptedTerms((current) => !current)}
              />
              <label htmlFor="terms-consent" className={styles.checkboxLabel}>
                {t('onboardingSetupPage.agreementLineOne.prefix')}
                <Link to="/terms" className={styles.link} onClick={stopCheckboxLinkToggle}>
                  {t('onboardingSetupPage.agreementLineOne.terms')}
                </Link>
                <span>{t('onboardingSetupPage.agreementLineOne.middle')}</span>
                <Link to="/policy" className={styles.link} onClick={stopCheckboxLinkToggle}>
                  {t('onboardingSetupPage.agreementLineOne.policy')}
                </Link>
                <span>{t('onboardingSetupPage.agreementLineOne.suffix')}</span>
                <Link to="/disclaimer" className={styles.link} onClick={stopCheckboxLinkToggle}>
                  {t('onboardingSetupPage.agreementLineOne.disclaimer')}
                </Link>
              </label>
            </div>

            <div className={styles.checkboxRow}>
              <input
                id="data-consent"
                type="checkbox"
                className={styles.checkbox}
                checked={acceptedDataProcessing}
                onChange={() => setAcceptedDataProcessing((current) => !current)}
              />
              <label htmlFor="data-consent" className={styles.checkboxLabel}>
                {t('onboardingSetupPage.agreementLineTwo')}
              </label>
            </div>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.continueButton}`}
          disabled={!canContinue}
          onClick={handleContinue}
        >
          {t('continue')}
        </button>

        <button
          type="button"
          className={`${styles.actionButton} ${styles.emergencyButton}`}
          onClick={() => setShowEmergencyModal(true)}
        >
          {t('emergency.button')}
        </button>
      </footer>

      <div className={styles.colorBar} aria-hidden="true">
        <span className={styles.colorBarTeal} />
        <span className={styles.colorBarYellow} />
        <span className={styles.colorBarOrange} />
        <span className={styles.colorBarRed} />
      </div>

      {showEmergencyModal && <Emergency onCloseModal={() => setShowEmergencyModal(false)} />}
    </div>
  );
}
